import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import { ITabItem } from 'components/tab';
import { useTabs } from 'components/tab/hooks';
import { RMSvgIconNames } from 'components/customComponents/CommonComponents/RMSvgIcon';
import LayersContainer from 'components/commandpanel/Layers/LayersContainer';
import Navigator from 'components/commandpanel/Navigator/Navigator';
import FocusLevel from 'components/commandpanel/FocusLevelThumbnail/FocusLevel';
import ImageTools from 'components/commandpanel/ExperimentPanel/ExperimentTools/ImageTools';
import {
  DisplayModes,
  GetUnifiedExperimentCanvasWidth,
  headerButtonsColor,
  headerIconSize,
  isDropWellCompare,
} from 'utils/Global';
import ScoresPanel from 'components/commandpanel/ExperimentPanel/ScoresPanel';
import ExperimentDashboardInfoPanel from 'components/commandpanel/ExperimentDashboardInfo/ExperimentDashboardInfoPanel';
import ImageNowSidePanel from 'components/commandpanel/ImageNow/ImageNowSidePanel';
import RMCustomButton from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import Harvesting from './harvesting';
import FindDrop from 'components/Experiment/FindDrop';
import { DEFAULT_FIND_DROP_WIDTH, DEFAULT_SIDE_PANEL_WIDTH } from './Constants';
import { useRMSelector } from 'redux/hooks';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import ImageTuningContainer from 'components/commandpanel/ImageTuning/ImageTuningContainer';
import { selectImageTuningValues } from 'redux/selectors/StateSelectors';
import { EProductEditions } from 'enums/EProductEditions';
import { isScoringFeatureEnabled } from 'components/Experiment/constants';

const getTabSelector = (title: string, iconName: RMSvgIconNames) => (
  <RMCustomButton
    title={title}
    iconProps={{ name: iconName, fill: headerButtonsColor, ...headerIconSize }}
    titleClass="header-button-title"
    contentContainerClass="tab-selectors-header-button-container"
    contentOrientation="Vertical"
  />
);

//TODO Change to Type & Params
export interface useExperimentHeaderPanelsProps {
  displayMode: string;
  selectedTab: string | number;
  setSelectedTab: Dispatch<SetStateAction<number>>;
  isButtonsDisabled: boolean;
  zoomInvocker: any;
  rmLite?: boolean;
  productEdition?: EProductEditions;
}

const useExperimentHeaderPanels = (props: useExperimentHeaderPanelsProps) => {
  const {
    displayMode,
    selectedTab = null,
    setSelectedTab,
    isButtonsDisabled,
    zoomInvocker,
    rmLite,
    productEdition,
  } = props;
  const [selectedDisplayIndex, setSelectedDisplayIndex] = useState(selectedTab);
  const [panelWidth, setPanelWidth] = useState(DEFAULT_SIDE_PANEL_WIDTH);
  const selectedExperiment = useRMSelector(selectExp);
  const imageTuningValues = useRMSelector((state) => selectImageTuningValues(state));
  const isDropTab = isDropWellCompare();
  const isScoringEnabled = isScoringFeatureEnabled(productEdition);
  const scorePanelTabItems: ITabItem[] = isScoringEnabled
    ? [
        {
          customTabSelectionItem: getTabSelector('Score', 'Scores'),
          content: selectedTab === 'score' && <ScoresPanel />,
          externalKey: 'score',
        },
      ]
    : [];
  const onTabSelectionChange = useCallback(
    (value) => {
      setSelectedTab(value);
      setSelectedDisplayIndex(value);
      setPanelWidth(
        value === 'find-drop1' ? DEFAULT_FIND_DROP_WIDTH : DEFAULT_SIDE_PANEL_WIDTH,
      );
      GetUnifiedExperimentCanvasWidth(
        100 -
          (value === 'find-drop1' ? DEFAULT_FIND_DROP_WIDTH : DEFAULT_SIDE_PANEL_WIDTH),
      );
    },
    [selectedTab],
  );

  useEffect(() => {
    setSelectedDisplayIndex(selectedTab);
  }, [displayMode, selectedTab]);

  useEffect(() => {
    zoomInvocker(0);
  }, [panelWidth]);

  const tabItems_: ITabItem[] = useMemo(() => {
    if (displayMode === DisplayModes.IMAGE) {
      return [
        {
          customTabSelectionItem: getTabSelector('Navigator', 'Navigator'),
          content: selectedTab === 'navigator' && selectedExperiment && <Navigator />,
          externalKey: 'navigator',
          width: panelWidth,
        },
        ...(!rmLite
          ? [
              {
                customTabSelectionItem: getTabSelector('Find Drops', 'FindDrop'),
                content: <FindDrop setPanelWidth={setPanelWidth} />,
                width: panelWidth,
                externalKey: 'find-drop',
              },
            ]
          : []),
        {
          customTabSelectionItem: getTabSelector('Image Now', 'ImageNow'),
          content: selectedTab === 'image-now' && (
            <ImageNowSidePanel onClose={() => setSelectedDisplayIndex(-1)} />
          ),
          externalKey: 'image-now',
        },
        {
          customTabSelectionItem: getTabSelector('Tools', 'Tools'),
          content: selectedTab === 'tools' && <ImageTuningContainer />,
          externalKey: 'tools',
          disabled: !imageTuningValues && !isDropTab,
        },
        ...scorePanelTabItems,
        {
          customTabSelectionItem: getTabSelector('Info', 'Info'),
          content: selectedTab === 'info' && <ExperimentDashboardInfoPanel />,
          externalKey: 'info',
        },
      ];
    } else if (displayMode === DisplayModes.SCORE) {
      return [
        {
          customTabSelectionItem: getTabSelector('Navigator', 'Navigator'),
          content: selectedTab === 'navigator' && selectedExperiment && <Navigator />,
          externalKey: 'navigator',
          width: panelWidth,
        },
        ...(!rmLite
          ? [
              {
                customTabSelectionItem: getTabSelector('Find Drops', 'FindDrop'),
                content: <FindDrop setPanelWidth={setPanelWidth} />,
                width: panelWidth,
                externalKey: 'find-drop',
              },
            ]
          : []),
        ...scorePanelTabItems,
        {
          customTabSelectionItem: getTabSelector('Info', 'Info'),
          content: selectedTab === 'info' && <ExperimentDashboardInfoPanel />,
          externalKey: 'info',
        },
      ];
    } else if (displayMode === DisplayModes.DESIGN) {
      return [
        {
          customTabSelectionItem: getTabSelector('Layers', 'Layers'),
          content: selectedTab === 'layers' && <LayersContainer />,
          externalKey: 'layers',
        },
        {
          customTabSelectionItem: getTabSelector('Find Drops', 'FindDrop'),
          content: <FindDrop setPanelWidth={setPanelWidth} />,
          width: panelWidth,
          externalKey: 'find-drop',
        },
        {
          customTabSelectionItem: getTabSelector('Info', 'Info'),
          content: selectedTab === 'info' && <ExperimentDashboardInfoPanel />,
          externalKey: 'info',
        },
      ];
    } else if (displayMode == DisplayModes.WELLIMAGE) {
      return [
        {
          customTabSelectionItem: getTabSelector('Navigator', 'Navigator'),
          content: selectedTab === 'navigator' && selectedExperiment && <Navigator />,
          externalKey: 'navigator',
        },
        {
          customTabSelectionItem: getTabSelector('Image Now', 'ImageNow'),
          content: selectedTab === 'image-now' && (
            <ImageNowSidePanel onClose={() => setSelectedDisplayIndex(-1)} />
          ),
          externalKey: 'image-now',
        },
        {
          customTabSelectionItem: getTabSelector('Focus', 'Focus'),
          content: selectedTab === 'focus' && <FocusLevel />,
          externalKey: 'focus',
        },
        ...(!rmLite && productEdition !== EProductEditions.PlateViewer
          ? [
              {
                customTabSelectionItem: getTabSelector('Harvesting', 'Harvesting'),
                content: selectedTab === 'harvesting' && <Harvesting />,
                externalKey: 'harvesting',
              },
            ]
          : []),
        {
          customTabSelectionItem: getTabSelector('Tools', 'Tools'),
          content: selectedTab === 'tools' && <ImageTools />,
          externalKey: 'tools',
          disabled: !imageTuningValues && !isDropTab,
        },
        ...scorePanelTabItems,
        {
          customTabSelectionItem: getTabSelector('Info', 'Info'),
          content: selectedTab === 'info' && <ExperimentDashboardInfoPanel />,
          externalKey: 'info',
        },
      ];
    } else if (displayMode === DisplayModes.SCREEN) {
      return [
        {
          customTabSelectionItem: getTabSelector('Layers', 'Layers'),
          content: selectedTab === 'layers' && <LayersContainer />,
          externalKey: 'layers',
        },
        {
          customTabSelectionItem: getTabSelector('Find Drops', 'FindDrop'),
          content: <FindDrop setPanelWidth={setPanelWidth} />,
          width: panelWidth,
          externalKey: 'find-drop',
        },
        {
          customTabSelectionItem: getTabSelector('Info', 'Info'),
          content: selectedTab === 'info' && (
            <ExperimentDashboardInfoPanel isOpenFromScreen={true} />
          ),
          externalKey: 'info',
        },
      ];
    } else if (displayMode === DisplayModes.WELLINGREDIENT && !rmLite) {
      return [
        {
          customTabSelectionItem: getTabSelector('Find Drops', 'FindDrop'),
          content: <FindDrop setPanelWidth={setPanelWidth} />,
          width: panelWidth,
          externalKey: 'find-drop',
        },
        {
          customTabSelectionItem: getTabSelector('Info', 'Info'),
          content: selectedTab === 'info' && <ExperimentDashboardInfoPanel />,
          externalKey: 'info',
        },
      ];
    } else {
      return [
        {
          customTabSelectionItem: null,
          content: null,
          externalKey: null,
        },
      ];
    }
  }, [displayMode, selectedTab, isButtonsDisabled, panelWidth, imageTuningValues]);

  const [tabSelectors, , selectedPanel] = useTabs(
    tabItems_,
    selectedDisplayIndex,
    onTabSelectionChange,
    isButtonsDisabled,
    true,
  );

  return [tabSelectors, selectedPanel as React.ReactNode];
};

export default useExperimentHeaderPanels;
